import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { trackLead } from "@/utils/metaPixel";
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Ticket, 
  Snowflake, 
  BookOpen, 
  Tag, 
  Users, 
  ChevronDown,
  Instagram,
  Heart
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LandingPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Bitte gib deinen Namen ein");
      return;
    }
    
    if (!email.trim()) {
      toast.error("Bitte gib deine E-Mail-Adresse ein");
      return;
    }
    
    if (!consent) {
      toast.error("Bitte stimme der Datenschutzerklärung zu");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API}/subscribe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          consent: consent,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Fire STANDARD Lead event - NO PARAMETERS
        trackLead();
        navigate("/danke");
      } else {
        toast.error(data.detail || data.message || "Ein Fehler ist aufgetreten");
      }
    } catch (error) {
      console.error("Subscription error:", error);
      toast.error("Verbindungsfehler. Bitte versuche es erneut.");
    } finally {
      setIsLoading(false);
    }
  };

  const scrollToForm = () => {
    const formElement = document.getElementById("signup-form");
    if (formElement) {
      formElement.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] font-body">
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center px-4 py-12 md:py-20">
        <div className="max-w-lg mx-auto w-full">
          {/* Brand Tag */}
          <div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B2332]/10 text-[#8B2332] text-sm font-medium mb-6 animate-fade-in-up"
            data-testid="brand-tag"
          >
            <Heart className="w-4 h-4" />
            <span>EDYT & BLOOM</span>
          </div>

          {/* Main Headline */}
          <h1 
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-tight mb-4 animate-fade-in-up stagger-1"
            data-testid="hero-headline"
          >
            Dein Expresspass fürs{" "}
            <span className="text-[#8B2332] italic">Hijab & Halal</span>{" "}
            Pop-up Store
          </h1>

          {/* Subline */}
          <p 
            className="text-lg md:text-xl text-[#6B6B6B] mb-4 animate-fade-in-up stagger-2"
            data-testid="hero-subline"
          >
            Kostenlos anmelden – schneller rein. Kein Aufpreis, nur Vorteile.
          </p>

          {/* Free Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-full text-green-700 text-sm font-medium mb-6 animate-fade-in-up stagger-2">
            <span>✓</span>
            <span>Anmeldung kostenlos – Eintritt 5€ vor Ort wie gewohnt</span>
          </div>

          {/* Event Facts */}
          <div 
            className="flex flex-wrap gap-2 mb-8 animate-fade-in-up stagger-3"
            data-testid="event-facts"
          >
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-[#2D2D2D] border border-[#DCD6CF]">
              <Calendar className="w-4 h-4 text-[#8B2332]" />
              So, 08.02.2026
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-[#2D2D2D] border border-[#DCD6CF]">
              <Clock className="w-4 h-4 text-[#8B2332]" />
              11–19 Uhr
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-[#2D2D2D] border border-[#DCD6CF]">
              <MapPin className="w-4 h-4 text-[#8B2332]" />
              Hamburg-Lohbrügge
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full text-sm text-[#2D2D2D] border border-[#DCD6CF]">
              <Ticket className="w-4 h-4 text-[#8B2332]" />
              Eintritt 5€ vor Ort
            </span>
          </div>

          {/* Signup Form */}
          <div 
            id="signup-form" 
            className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-[#EFEBE5] animate-fade-in-up stagger-4"
            data-testid="signup-form-container"
          >
            <h2 className="font-heading text-2xl text-[#2D2D2D] mb-2">
              Expresspass sichern
            </h2>
            <p className="text-[#6B6B6B] text-sm mb-4">
              Kostenlos anmelden → Expresskasse nutzen → 5€ Eintritt vor Ort zahlen
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Dein Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332] rounded-lg"
                  data-testid="input-name"
                  disabled={isLoading}
                />
              </div>
              
              <div>
                <Input
                  type="email"
                  placeholder="Deine E-Mail-Adresse"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 bg-[#FAF7F2] border-[#DCD6CF] focus:border-[#8B2332] focus:ring-[#8B2332] rounded-lg"
                  data-testid="input-email"
                  disabled={isLoading}
                />
              </div>

              <div className="flex items-start gap-3">
                <Checkbox
                  id="consent"
                  checked={consent}
                  onCheckedChange={setConsent}
                  className="mt-1 border-[#8B2332] data-[state=checked]:bg-[#8B2332] data-[state=checked]:border-[#8B2332]"
                  data-testid="checkbox-consent"
                  disabled={isLoading}
                />
                <label 
                  htmlFor="consent" 
                  className="text-sm text-[#6B6B6B] leading-relaxed cursor-pointer"
                >
                  Ich möchte Infos zum Event, Verlosungen und kuratierte Deals per E-Mail erhalten. Abmeldung jederzeit möglich.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full h-14 bg-[#8B2332] hover:bg-[#7A1E2B] text-white text-lg font-medium rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                data-testid="submit-button"
                disabled={isLoading}
              >
                {isLoading ? "Wird angemeldet..." : "Expresspass sichern"}
              </Button>
            </form>
          </div>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-8 animate-bounce">
            <ChevronDown className="w-6 h-6 text-[#C4A4A0]" />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 px-4 bg-white" data-testid="benefits-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] text-center mb-12">
            Das bekommst du mit dem Expresspass
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Benefit 1 */}
            <div 
              className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#EFEBE5] hover:shadow-md transition-shadow"
              data-testid="benefit-fast-lane"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2332]/10 flex items-center justify-center mb-4">
                <Snowflake className="w-6 h-6 text-[#8B2332]" />
              </div>
              <h3 className="font-heading text-xl text-[#2D2D2D] mb-2">
                Keine Lust zu frieren?
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                Extra-Einlass, wenn es voll ist. Zeig einfach deinen Expresspass am Eingang – besonders bei langen Schlangen. Damit es nicht zu langen Wartezeiten kommt.
              </p>
            </div>

            {/* Benefit 2 */}
            <div 
              className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#EFEBE5] hover:shadow-md transition-shadow"
              data-testid="benefit-raffle"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2332]/10 flex items-center justify-center mb-4">
                <BookOpen className="w-6 h-6 text-[#8B2332]" />
              </div>
              <h3 className="font-heading text-xl text-[#2D2D2D] mb-2">
                Besondere Verlosungen
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                Mit unserem E-Mail-Newsletter bekommst du Zugang zu besonderen Verlosungen.
              </p>
            </div>

            {/* Benefit 3 */}
            <div 
              className="bg-[#FAF7F2] rounded-2xl p-6 border border-[#EFEBE5] hover:shadow-md transition-shadow"
              data-testid="benefit-deals"
            >
              <div className="w-12 h-12 rounded-full bg-[#8B2332]/10 flex items-center justify-center mb-4">
                <Tag className="w-6 h-6 text-[#8B2332]" />
              </div>
              <h3 className="font-heading text-xl text-[#2D2D2D] mb-2">
                Kuratierte Deals
              </h3>
              <p className="text-[#6B6B6B] text-sm leading-relaxed">
                Exklusive Rabatte bei ausgewählten Brands – einfach den Mail-Code am Stand zeigen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24 px-4 bg-[#8B2332]" data-testid="social-proof-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-16 mb-8">
            <div>
              <div className="font-heading text-5xl md:text-6xl text-white mb-2">
                ~1.200
              </div>
              <p className="text-white/80 text-lg">Besucherinnen beim letzten Mal</p>
            </div>
            <div className="hidden md:block w-px h-20 bg-white/20"></div>
            <div>
              <div className="font-heading text-5xl md:text-6xl text-white mb-2">
                100
              </div>
              <p className="text-white/80 text-lg">Goodie Bags für die ersten Gäste</p>
            </div>
          </div>
          
          <p className="text-white/90 text-lg md:text-xl max-w-xl mx-auto mb-8">
            Mit dem Expresspass kommst du schneller rein – kostenlos anmelden, 5€ Eintritt vor Ort.
          </p>

          <Button
            onClick={scrollToForm}
            className="bg-white text-[#8B2332] hover:bg-[#FAF7F2] rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
            data-testid="cta-social-proof"
          >
            Kostenlos Expresspass sichern
          </Button>
        </div>
      </section>

      {/* Event Info Section - From Flyer */}
      <section className="py-16 md:py-24 px-4 bg-white" data-testid="event-info-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] text-center mb-4">
            Hijab & Halal Pop-up Store
          </h2>
          <p className="text-[#6B6B6B] text-center text-lg mb-12 max-w-2xl mx-auto">
            Der größte muslimische Pop-up Store in Hamburg
          </p>

          {/* Event Details Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            <div className="bg-[#FAF7F2] rounded-xl p-5 text-center border border-[#EFEBE5]">
              <Calendar className="w-8 h-8 text-[#8B2332] mx-auto mb-3" />
              <div className="font-heading text-lg text-[#2D2D2D]">Sonntag</div>
              <div className="text-[#8B2332] font-bold text-xl">08.02.2026</div>
            </div>
            <div className="bg-[#FAF7F2] rounded-xl p-5 text-center border border-[#EFEBE5]">
              <Clock className="w-8 h-8 text-[#8B2332] mx-auto mb-3" />
              <div className="font-heading text-lg text-[#2D2D2D]">Öffnungszeiten</div>
              <div className="text-[#8B2332] font-bold text-xl">11–19 Uhr</div>
            </div>
            <div className="bg-[#FAF7F2] rounded-xl p-5 text-center border border-[#EFEBE5]">
              <MapPin className="w-8 h-8 text-[#8B2332] mx-auto mb-3" />
              <div className="font-heading text-lg text-[#2D2D2D]">Location</div>
              <div className="text-[#8B2332] font-bold text-sm">Grand Palais, Osterrade 20</div>
              <div className="text-[#6B6B6B] text-xs">21031 Hamburg-Lohbrügge</div>
            </div>
            <div className="bg-[#FAF7F2] rounded-xl p-5 text-center border border-[#EFEBE5]">
              <Ticket className="w-8 h-8 text-[#8B2332] mx-auto mb-3" />
              <div className="font-heading text-lg text-[#2D2D2D]">Eintritt</div>
              <div className="text-[#8B2332] font-bold text-xl">5,00 €</div>
              <div className="text-[#6B6B6B] text-xs">vor Ort (bar)</div>
            </div>
          </div>

          {/* What's There */}
          <div className="bg-[#8B2332]/5 rounded-2xl p-6 md:p-8 border border-[#8B2332]/20">
            <h3 className="font-heading text-2xl text-[#2D2D2D] mb-6 text-center">
              Was dich erwartet
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-3xl mb-2">👗</div>
                <div className="font-medium text-[#2D2D2D]">Modest Fashion</div>
                <div className="text-[#6B6B6B] text-sm">Abayas, Kleider, Outfits</div>
              </div>
              <div>
                <div className="text-3xl mb-2">🧕</div>
                <div className="font-medium text-[#2D2D2D]">Hijabs</div>
                <div className="text-[#6B6B6B] text-sm">Alle Styles & Farben</div>
              </div>
              <div>
                <div className="text-3xl mb-2">🍽️</div>
                <div className="font-medium text-[#2D2D2D]">Food & Drinks</div>
                <div className="text-[#6B6B6B] text-sm">Catering, Matcha & mehr</div>
              </div>
              <div>
                <div className="text-3xl mb-2">🎁</div>
                <div className="font-medium text-[#2D2D2D]">Kids & Geschenke</div>
                <div className="text-[#6B6B6B] text-sm">Bücher, Spielzeug, Deko</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect Section */}
      <section className="py-16 md:py-24 px-4 bg-[#FAF7F2]" data-testid="expectations-section">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] text-center mb-4">
            Viele neue Brands!
          </h2>
          <p className="text-[#6B6B6B] text-center text-lg mb-12 max-w-2xl mx-auto">
            Alles, was eine Muslima braucht – an einem Ort
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 text-center border border-[#EFEBE5]">
              <div className="font-heading text-3xl text-[#8B2332] mb-1">50+</div>
              <p className="text-[#6B6B6B] text-sm">Muslimische Brands</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center border border-[#EFEBE5]">
              <div className="font-heading text-xl text-[#2D2D2D] mb-1">Modest Fashion</div>
              <p className="text-[#6B6B6B] text-sm">Abayas & Outfits</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center border border-[#EFEBE5]">
              <div className="font-heading text-xl text-[#2D2D2D] mb-1">Hijabs</div>
              <p className="text-[#6B6B6B] text-sm">Alle Styles</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center border border-[#EFEBE5]">
              <div className="font-heading text-xl text-[#2D2D2D] mb-1">Kids</div>
              <p className="text-[#6B6B6B] text-sm">Bücher, Spielzeug & Süßkram</p>
            </div>
          </div>

          {/* Brand Logos Image */}
          <div className="mt-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_c65dc5ea-4e78-4ddb-9ed3-65d5cc51503a/artifacts/geuv4xgp_image.png"
              alt="Teilnehmende Brands - Viele neue Brands! Und 100 Goodie Bags für die ersten Gäste."
              className="w-full max-w-2xl mx-auto rounded-2xl shadow-lg"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 px-4 bg-white" data-testid="faq-section">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] text-center mb-12">
            Häufige Fragen
          </h2>

          {/* Eintritt & Expresspass */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Eintritt & Expresspass</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="entry-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Brauche ich ein Ticket, um reinzukommen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Nein. Du kannst einfach vor Ort Eintritt bezahlen. Wenn du dich hier für den Expresspass anmeldest, kommst du deutlich schneller rein und bekommst zusätzlich kleine und große Vorteile.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="entry-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Wie viel muss man zahlen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Der Eintritt kostet 5 €.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="entry-3" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Ist es immer sehr voll?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Am Anfang ist es oft recht voll. Zwischendurch wird es meist entspannter. Gegen Abend kann es nochmal richtig voll werden.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Kinder & Familie */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Kinder & Familie</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="kids-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Darf ich mein Kind mitnehmen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Natürlich. Dein Kind darf mitkommen. Ab 10 Jahren zahlt dein Kind Eintritt. Das Event ist eher auf Erwachsene ausgerichtet: Es gibt zwar Kinderartikel, aber kein Kinderprogramm.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="kids-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Ist es dort barrierefrei (Kinderwagen/Rollstuhl)?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Ja, absolut. Du kommst mit Kinderwagen und Rollstuhl problemlos rein.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Essen & Getränke */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Essen & Getränke</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="food-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Kann man dort sitzen und essen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Ja, absolut. Es gibt sehr leckeres Essen und du kannst den ganzen Tag entspannt dort verbringen.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="food-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Gibt es auch Matcha Latte?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Ja, es gibt Matcha Latte und auch andere Heißgetränke.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Anfahrt & Parken */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Anfahrt & Parken</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="travel-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Kommt man auch mit dem HVV dorthin?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Ja. Der Weg ist ziemlich simpel: Gib am besten die Straße in der HVV-App ein. Grob gesagt fährst du Richtung Mümmelmannsberg oder Bergedorf und nimmst von dort einen Bus.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="travel-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Gibt es dort kostenlose Parkplätze?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Du kannst an der Straße parken. Außerdem kannst du auch auf dem Aldi-Parkplatz in der Nähe parken.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Für wen ist das Event? */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Für wen ist das Event?</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="who-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Ich trage kein Kopftuch – kann ich trotzdem kommen?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Natürlich und selbstverständlich. Jede Frau ist herzlich willkommen.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="who-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Für wen ist das Event geeignet?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Für muslimische Frauen (und alle Frauen, die Lust haben), die sich mit Dingen eindecken möchten wie Kleidung, Hijabs, Datteln, Kinderartikel und mehr.
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Orga & Newsletter */}
          <h3 className="font-heading text-xl text-[#8B2332] mb-4 mt-8">Orga & Newsletter</h3>
          <Accordion type="single" collapsible className="space-y-3">
            <AccordionItem value="orga-1" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Bekomme ich Spam?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Nein, auf keinen Fall. Du bekommst nur relevante E-Mails – z. B. besondere Deals und Infos rund um den Pop-up-Store und kommende Pop-up-Stores.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="orga-2" className="bg-[#FAF7F2] rounded-xl border border-[#EFEBE5] px-6">
              <AccordionTrigger className="text-[#2D2D2D] font-medium hover:no-underline">
                Wo findet es statt?
              </AccordionTrigger>
              <AccordionContent className="text-[#6B6B6B]">
                Diesmal im Grand Palais in der Osterrade. Schau dir gerne auch nochmal den Flyer an.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-[#FAF7F2]" data-testid="final-cta-section">
        <div className="max-w-lg mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-[#2D2D2D] mb-4">
            Bereit?
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-8">
            Sichere dir jetzt deinen Expresspass.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-[#8B2332] hover:bg-[#7A1E2B] text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:-translate-y-1 hover:shadow-xl"
            data-testid="cta-final"
          >
            Expresspass sichern
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-white border-t border-[#EFEBE5]" data-testid="footer">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2 text-[#6B6B6B]">
              <Heart className="w-4 h-4 text-[#8B2332]" />
              <span className="font-medium text-[#2D2D2D]">EDYT & BLOOM</span>
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <a 
                href="/impressum" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-impressum"
              >
                Impressum
              </a>
              <a 
                href="/datenschutz" 
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-datenschutz"
              >
                Datenschutz
              </a>
              <a 
                href="https://www.instagram.com/edytandbloom/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#6B6B6B] hover:text-[#8B2332] transition-colors"
                data-testid="footer-instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <p className="text-center text-[#6B6B6B] text-xs mt-6">
            Du kannst dich jederzeit abmelden.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
